<template>
    <div class="column is-12">
        <!-- Provider Auswahl + Stripe Bereich -->
        <div class="box-shadow mt-2 p-2" v-if="allowOnlinePayment">
            <!-- Stripe Onboarding Result Notice -->
            <div
                v-if="onboardingNotice"
                class="notification mb-2"
                :class="onboardingNotice.typeClass"
            >
                <span class="icon mr-1">
                    <i class="fa" :class="onboardingNotice.iconClass" aria-hidden="true"></i>
                </span>
                <span>{{ onboardingNotice.message }}</span>
            </div>

            <h1>{{ $t("settings.payment.providerConfigLabel") }}</h1>

            <div class="is-variable is-4">
                <!-- Zahlungsanbieter + Hinweis nebeneinander -->
                <div class="columns is-6 is-horizontal is-align-items-center">
                    <div class="column">
                        <div class="field-label is-normal">
                            <label class="label">{{ $t("settings.payment.providerLabel") }}</label>
                        </div>
                        <div class="field-body">
                            <div class="field is-narrow" v-if="!hasStripeAccountId">
                                <div class="control">
                                    <div class="select">
                                        <select
                                            v-model="sp.value.payment_provider"
                                            @change="handleProviderChange"
                                        >
                                            <option value="">{{
                                                $t("settings.payment.noProvider")
                                            }}</option>
                                            <option value="STRIPE">Stripe</option>
                                        </select>
                                    </div>
                                </div>
                                <p v-if="sp.formErrors.payment_provider" class="help is-danger">
                                    {{
                                        Array.isArray(sp.formErrors.payment_provider)
                                            ? sp.formErrors.payment_provider[0]
                                            : sp.formErrors.payment_provider
                                    }}
                                </p>
                            </div>
                            <span v-else>{{ sp.value.payment_provider }}</span>
                        </div>
                    </div>
                    <div
                        class="column"
                        v-if="sp.value.payment_provider === 'STRIPE' && !hasStripeAccountId"
                    >
                        <div class="notification is-info is-light mb-0">
                            {{ $t("settings.payment.stripe.connect.notice") }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="sp.value.payment_provider === 'STRIPE'" class="mt-3">
                <!-- Hauptaktion: abhängig von account_id entweder Admin öffnen oder verbinden -->
                <div class="field">
                    <template v-if="hasStripeAccountId">
                        <div class="is-flex is-align-items-flex-start is-flex-wrap-wrap">
                            <div class="mr-3">
                                <button
                                    class="button is-primary"
                                    @click.prevent="openStripeDashboard"
                                >
                                    {{ $t("settings.payment.stripe.button.openDashboard") }}
                                </button>
                                <p class="help mt-1">
                                    {{ $t("settings.payment.stripe.help.openDashboard") }}
                                </p>
                            </div>
                            <div>
                                <Modal
                                    :title="$t('settings.payment.stripe.disconnectTitle')"
                                    :submitButton="
                                        $t('settings.payment.stripe.disconnectConfirmButton')
                                    "
                                    :abortButton="$t('generic.cancel')"
                                    :isLoading="stripeDisconnectLoading"
                                    :onSubmit="confirmDisconnectStripe"
                                >
                                    <template #activator>
                                        <button class="button is-danger">
                                            {{ $t("settings.payment.stripe.button.disconnect") }}
                                        </button>
                                    </template>
                                    <div class="content" style="white-space: pre-line">
                                        {{ $t("settings.payment.stripe.disconnectMessage") }}
                                    </div>
                                </Modal>
                                <p class="help mt-1">
                                    {{ $t("settings.payment.stripe.help.disconnect") }}
                                </p>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <!-- Express Connect -->
                        <!--
            <button class="button is-primary"
                    :class="{ 'is-loading': stripeConnectLoading }"
                    @click.prevent="connectStripe(true)">
              {{ $t('settings.payment.stripe.button.connectExpress') || $t('settings.payment.stripe.button.connect') }}
            </button>
            <p class="help">{{ $t('settings.payment.stripe.help.connectExpress') || $t('settings.payment.stripe.help.connect') }}</p>

            <hr />#
            -->
                        <!-- Standard Connect -->
                        <button
                            class="button is-primary"
                            :class="{ 'is-loading': stripeConnectLoading }"
                            @click.prevent="connectStripe(false)"
                        >
                            {{ $t("settings.payment.stripe.button.connectStandard") }}
                        </button>
                        <p class="help">{{ $t("settings.payment.stripe.help.connectStandard") }}</p>
                    </template>
                </div>

                <!-- Provider-Status / Details -->
                <div class="box mt-3" v-if="stripeProviderState">
                    <h2 class="title is-6 mb-3">
                        {{ $t("settings.payment.stripe.details.header") }}
                    </h2>
                    <!-- Oberer Statusbereich -->
                    <div class="mb-3">
                        <span
                            class="tag"
                            v-if="stripeProviderState.account_type"
                            :class="{
                                'is-black': stripeProviderState.account_type === 'express',
                                'is-info': stripeProviderState.account_type !== 'express'
                            }"
                        >
                            {{ stripeProviderState.account_type }}
                        </span>
                        <span
                            class="tag"
                            :class="{
                                'is-success':
                                    stripeProviderState.connected &&
                                    stripeProviderState.status === 'active',
                                'is-warning':
                                    stripeProviderState.status &&
                                    stripeProviderState.status !== 'active',
                                'is-light': !stripeProviderState.status
                            }"
                        >
                            {{
                                stripeProviderState.status ||
                                    (stripeProviderState.connected
                                        ? $t("settings.payment.stripe.status.connected")
                                        : $t("settings.payment.stripe.status.notConnected"))
                            }}
                        </span>
                        <span
                            v-if="
                                stripeProviderState.can_select_online_payment &&
                                    stripeProviderState.connected &&
                                    !isOnlinePaymentSelected
                            "
                            class="ml-2 has-text-grey"
                        >
                            You can select now
                        </span>
                        <div
                            v-if="
                                !stripeProviderState.can_select_online_payment &&
                                    stripeProviderState.connected
                            "
                            class="ml-2 has-text-grey"
                        >
                            You can not select Onlinepayment, please activate your Stripe account
                        </div>
                        <span v-if="stripeProviderState.status_message" class="ml-2 has-text-grey">
                            {{ stripeProviderState.status_message }}
                        </span>
                        <span v-if="stripeProviderState.message" class="ml-2 has-text-grey">
                            {{ stripeProviderState.message }}
                        </span>
                    </div>

                    <!-- Capabilities -->
                    <div v-if="stripeProviderState.capabilities" class="mb-4">
                        <label class="label is-small">{{
                            $t("settings.payment.stripe.capabilities.label")
                        }}</label>
                        <div class="tags">
                            <span
                                class="tag"
                                :class="
                                    stripeProviderState.capabilities.charges_enabled
                                        ? 'is-success'
                                        : 'is-light'
                                "
                            >
                                {{ $t("settings.payment.stripe.capabilities.charges") }}
                            </span>
                            <span
                                class="tag"
                                :class="
                                    stripeProviderState.capabilities.payouts_enabled
                                        ? 'is-success'
                                        : 'is-light'
                                "
                            >
                                {{ $t("settings.payment.stripe.capabilities.payouts") }}
                            </span>
                            <span
                                class="tag"
                                :class="
                                    stripeProviderState.capabilities.details_submitted
                                        ? 'is-success'
                                        : 'is-light'
                                "
                            >
                                {{ $t("settings.payment.stripe.capabilities.detailsSubmitted") }}
                            </span>
                        </div>
                    </div>

                    <div class="columns is-variable is-4">
                        <div class="column is-12 is-6-desktop">
                            <!-- Business Infos -->
                            <div v-if="stripeProviderState.business" class="mb-4">
                                <label class="label is-small">{{
                                    $t("settings.payment.stripe.business.label")
                                }}</label>
                                <div class="content is-small">
                                    <p v-if="stripeProviderState.business.name">
                                        <strong>{{
                                            $t("settings.payment.stripe.business.name")
                                        }}</strong>
                                        <span class="ml-1">{{
                                            stripeProviderState.business.name
                                        }}</span>
                                    </p>
                                    <p v-if="stripeProviderState.business.url">
                                        <strong>{{
                                            $t("settings.payment.stripe.business.website")
                                        }}</strong>
                                        <a
                                            class="ml-1"
                                            target="_blank"
                                            :href="
                                                (stripeProviderState.business.url || '').match(
                                                    /^https?:\/\//
                                                )
                                                    ? stripeProviderState.business.url
                                                    : 'https://' + stripeProviderState.business.url
                                            "
                                        >
                                            {{ stripeProviderState.business.url }}
                                        </a>
                                    </p>
                                    <p v-if="stripeProviderState.business.email">
                                        <strong>{{
                                            $t("settings.payment.stripe.business.email")
                                        }}</strong>
                                        <a
                                            class="ml-1"
                                            :href="'mailto:' + stripeProviderState.business.email"
                                            >{{ stripeProviderState.business.email }}</a
                                        >
                                    </p>
                                </div>
                            </div>

                            <!-- Bankkonto -->
                            <div v-if="stripeProviderState.bank_account" class="mb-4">
                                <label class="label is-small">{{
                                    $t("settings.payment.stripe.bank.label")
                                }}</label>
                                <div class="content is-small">
                                    <p v-if="stripeProviderState.bank_account.bank_name">
                                        <strong>{{
                                            $t("settings.payment.stripe.bank.bank")
                                        }}</strong>
                                        <span class="ml-1">{{
                                            stripeProviderState.bank_account.bank_name
                                        }}</span>
                                    </p>
                                    <p v-if="stripeProviderState.bank_account.last4">
                                        <strong>{{
                                            $t("settings.payment.stripe.bank.account")
                                        }}</strong>
                                        <span class="ml-1"
                                            >•••• {{ stripeProviderState.bank_account.last4 }}</span
                                        >
                                    </p>
                                    <p>
                                        <span v-if="stripeProviderState.bank_account.country"
                                            ><strong>{{
                                                $t("settings.payment.stripe.bank.country")
                                            }}</strong
                                            ><span class="ml-1">{{
                                                stripeProviderState.bank_account.country
                                            }}</span></span
                                        >
                                        <span
                                            v-if="stripeProviderState.bank_account.currency"
                                            class="ml-3"
                                            ><strong>{{
                                                $t("settings.payment.stripe.bank.currency")
                                            }}</strong
                                            ><span class="ml-1">{{
                                                stripeProviderState.bank_account.currency
                                            }}</span></span
                                        >
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="column is-12 is-6-desktop">
                            <!-- Auszahlungsrhythmus -->
                            <div v-if="stripeProviderState.payouts" class="mb-4">
                                <label class="label is-small">{{
                                    $t("settings.payment.stripe.payouts.label")
                                }}</label>
                                <div class="content is-small">
                                    <p v-if="stripeProviderState.payouts.description">
                                        {{ stripeProviderState.payouts.description }}
                                    </p>
                                    <p v-else>
                                        <span v-if="stripeProviderState.payouts.interval"
                                            ><strong>{{
                                                $t("settings.payment.stripe.payouts.interval")
                                            }}</strong
                                            ><span class="ml-1">{{
                                                stripeProviderState.payouts.interval
                                            }}</span></span
                                        >
                                        <span
                                            v-if="
                                                stripeProviderState.payouts.delay_days !== undefined
                                            "
                                            class="ml-3"
                                            ><strong>{{
                                                $t("settings.payment.stripe.payouts.delay")
                                            }}</strong
                                            ><span class="ml-1"
                                                >{{ stripeProviderState.payouts.delay_days }}
                                                {{
                                                    $t("settings.payment.stripe.payouts.days")
                                                }}</span
                                            ></span
                                        >
                                    </p>
                                </div>
                            </div>

                            <!-- Weitere Angaben -->
                            <div class="is-size-7">
                                <p v-if="stripeProviderState.statement_descriptor">
                                    <strong>{{
                                        $t("settings.payment.stripe.statement.label")
                                    }}</strong>
                                    <span class="ml-1">{{
                                        stripeProviderState.statement_descriptor
                                    }}</span>
                                </p>
                                <p v-if="stripeProviderState.account_id" class="mt-1">
                                    <strong>{{
                                        $t("settings.payment.stripe.accountId.label")
                                    }}</strong>
                                    <code class="ml-1" style="word-break: break-all;">{{
                                        stripeProviderState.account_id
                                    }}</code>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Zahlarten-Bereich: Stellplätze und Mietobjekte nebeneinander -->
        <div class="columns is-variable is-3">
            <div class="column is-12 is-6-desktop">
                <div class="box-shadow p-2">
                    <div>
                        <h2>{{ $t("settings.deposit.header_pitches") }}</h2>
                        <div class="mt-2">
                            <label class="label">{{ $t("settings.payment.systemLabel") }}</label>
                            <div class="select">
                                <select v-model="sp.value.payment_system_pitches">
                                    <option
                                        v-for="(label, val) in filteredPaymentSystems"
                                        :key="val"
                                        :value="val"
                                    >
                                        {{ label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="mt-2" v-if="sp.value.payment_system_pitches !== 'ON_SITE'">
                            <h3 class="mb-2">{{ $t("settings.deposit.headline") }}</h3>
                            <p>{{ $t("settings.deposit.copy") }}</p>
                            <p>
                                <slider
                                    class="settings__deposit__slider"
                                    v-model="sp.value.deposit_percent"
                                    :min="sp.depositInfo.min_percent"
                                    :max="sp.depositInfo.max_percent"
                                    :step="5"
                                    @change="updateDeposit"
                                ></slider>
                                <span class="settings__deposit__percent"
                                    >{{ sp.value.deposit_percent }} %</span
                                >
                            </p>
                        </div>

                        <div
                            class="columns mt-4"
                            v-if="sp.value.payment_system_pitches !== 'ON_SITE'"
                        >
                            <div class="column is-6">
                                <label
                                    for="minimum_fixed_deposit"
                                    class="label edit-box__includes-inline-input"
                                    >{{ $t("settings.deposit.fixed.labels.campsites") }}</label
                                >
                            </div>
                            <div class="column is-6 text-with-input">
                                <form-input
                                    id="minimum_fixed_deposit"
                                    class="edit-box__inline-input is-inline"
                                    v-model="sp.value.minimum_fixed_deposit"
                                    :options="{ inputtype: 'number', min: 0, step: 1 }"
                                    :error="sp.formErrors.minimum_fixed_deposit"
                                >
                                </form-input>

                                <span class="edit-box__includes-inline-input is-inline-block">{{
                                    $getDefaultCurrency().symbol
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="column is-12 is-6-desktop">
                <div class="box-shadow p-2">
                    <div>
                        <h2>{{ $t("settings.deposit.header_rentals") }}</h2>
                        <div class="mt-2">
                            <label class="label">{{ $t("settings.payment.systemLabel") }}</label>
                            <div class="select">
                                <select v-model="sp.value.payment_system_rentals">
                                    <option
                                        v-for="(label, val) in filteredPaymentSystems"
                                        :key="val"
                                        :value="val"
                                    >
                                        {{ label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="mt-2" v-if="sp.value.payment_system_rentals !== 'ON_SITE'">
                            <h3 class="mb-2">{{ $t("settings.deposit.headline") }}</h3>
                            <p>{{ $t("settings.deposit.copy_rental") }}</p>
                            <p>
                                <slider
                                    class="settings__deposit__slider"
                                    v-model="sp.value.accommodation_deposit_percent"
                                    :min="sp.depositInfo.min_percent"
                                    :max="sp.depositInfo.max_percent"
                                    :step="5"
                                    @change="updateAccommodationDeposit"
                                ></slider>
                                <span class="settings__deposit__percent"
                                    >{{ sp.value.accommodation_deposit_percent }} %</span
                                >
                            </p>
                        </div>

                        <div
                            class="columns mt-4"
                            v-if="sp.value.payment_system_rentals !== 'ON_SITE'"
                        >
                            <div class="column is-6">
                                <label
                                    for="accommodation_minimum_fixed_deposit"
                                    class="label edit-box__includes-inline-input"
                                    >{{ $t("settings.deposit.fixed.labels.accommodations") }}</label
                                >
                            </div>
                            <div class="column is-6 text-with-input">
                                <form-input
                                    id="accommodation_minimum_fixed_deposit"
                                    class="edit-box__inline-input is-inline"
                                    v-model="sp.value.accommodation_minimum_fixed_deposit"
                                    :options="{ inputtype: 'number', min: 0, step: 1 }"
                                    :error="sp.formErrors.accommodation_minimum_fixed_deposit"
                                >
                                </form-input>

                                <span class="edit-box__includes-inline-input is-inline-block">{{
                                    $getDefaultCurrency().symbol
                                }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Slider from 'vue-bulma-slider';
import Modal from '../../modal/modal';
import Multiselect from 'vue-multiselect';
import { FormInput } from '../../index';
import { Api } from '../../../../api';
import { AppModel } from '../../../../models';
import { EventBus } from '../../../../utils';

export default {
    name: 'PaymentTab',
    inject: ['settingsParent'],
    components: { Slider, Modal, Multiselect, FormInput },
    data() {
        return {
            stripeConnectLoading: false,
            stripeDisconnectLoading: false,
            onboardingNotice: null,
        };
    },
    created() {
        // Verarbeite Stripe-Onboarding-Query einmalig, wenn Tab aktiv ist
        this.handleStripeOnboardingQuery();
    },
    computed: {
        sp() { return this.settingsParent; },
        allowOnlinePayment() {
            console.log('gk-debug', 'allowOnlinePayment', AppModel.stripeConfigured);
            return AppModel.stripeConfigured;
        },
        filteredPaymentSystems() {
            const systems = this.getPaymentSystems();
            if(this.allowOnlinePayment && this.hasValidStripeAccountId) return systems;
            console.log('gk-debug', 'filteredPaymentSystems', 'stripeAccountId', this.stripeProviderState);
            const remove = ['ONLINE'];
            return Object.fromEntries(Object.entries(systems).filter(([key]) => !remove.includes(key)));
        },
        stripeProviderState() {
            const v = (this.sp && this.sp.value) || {};
            return v.provider_state || v.payment_provider_state || null;
        },
        hasStripeAccountId() {
            const s = this.stripeProviderState;
            return !!(s && s.account_id);
        },
        hasValidStripeAccountId() {
            if(!this.hasStripeAccountId) return false;
            const s = this.stripeProviderState;
            if(!s || !s.can_select_online_payment) return false;
            return true;
        },
        isOnlinePaymentSelected() {
            return (this.sp.value.payment_system_pitches === 'ONLINE' || this.sp.value.payment_system_rentals === 'ONLINE');
        }
    },
    methods: {
        getPaymentSystems() {
            return this.$t('settings.payment.systems');
        },
        updateDeposit(val) {
            this.sp.value.deposit_percent = parseInt(val, 10);
        },
        updateAccommodationDeposit(val) {
            this.sp.value.accommodation_deposit_percent = parseInt(val, 10);
        },
        handleProviderChange() {
            if(this.sp.value.payment_provider === 'STRIPE') {
                if(this.sp.value.payment_provider_config === null) {
                    this.$set(this.sp.value, 'payment_provider_config', {});
                }
            }
        },
        async connectStripe(express) {
            try {
                this.stripeConnectLoading = true;
                let url = (this.sp.value && this.sp.value.payment_provider_config && this.sp.value.payment_provider_config.onboarding_url) || null;
                if(!url) {
                    const res = await Api.payment.getStripeOnboardingUrl(express);
                    const data = (res && (res.data || res.body)) || null;
                    if(data && typeof data === 'object') {
                        url = data.url || data.onboarding_url || (data.data && (data.data.url || data.data.onboarding_url)) || null;
                    } else if(typeof data === 'string') {
                        url = data;
                    } else if(res && typeof res === 'string') {
                        url = res;
                    }
                }
                if(url) {
                    window.open(url, '_blank');
                } else {
                    this.$toast && this.$toast.open({
                        message: this.$t('settings.payment.stripe.errors.onboardingUrlNotFound'),
                        type: 'is-danger',
                    });
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                this.$toast && this.$toast.open({
                    message: this.$t('settings.payment.stripe.errors.onboardingUrlFailed'),
                    type: 'is-danger',
                });
            } finally {
                this.stripeConnectLoading = false;
            }
        },
        async openStripeDashboard() {
            window.open('https://dashboard.stripe.com/', '_blank');
        },
        async confirmDisconnectStripe() {
            try {
                if(!this.hasStripeAccountId) return;
                this.stripeDisconnectLoading = true;
                await Api.payment.disconnectStripe();
                this.$toast && this.$toast.open({
                    message: this.$t ? (this.$t('settings.payment.stripe.disconnectedSuccess') || 'Stripe-Verbindung wurde gelöst.') : 'Stripe-Verbindung wurde gelöst.',
                    type: 'is-success',
                });
                window.location.reload();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                try {
                    const details = this.stringifyError(e);
                    EventBus && EventBus.$emit && EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t ? (this.$t('settings.payment.stripe.errors.disconnectFailed') || 'Trennen der Stripe-Verbindung fehlgeschlagen.') : 'Trennen der Stripe-Verbindung fehlgeschlagen.'}</h3>`,
                        body: `<pre style="white-space: pre-wrap; max-height: 50vh; overflow:auto;">${this.escapeHtml(details)}</pre>`,
                        type: 'danger',
                        direction: 'Up',
                        duration: 15000,
                    });
                } catch (ignored) {
                    this.$toast && this.$toast.open({
                        message: this.$t ? (this.$t('settings.payment.stripe.errors.disconnectFailed') || 'Trennen der Stripe-Verbindung fehlgeschlagen.') : 'Trennen der Stripe-Verbindung fehlgeschlagen.',
                        type: 'is-danger',
                    });
                }
            } finally {
                this.stripeDisconnectLoading = false;
            }
        },
        stringifyError(err) {
            try {
                const payload = err && (err.body || err.data || (err.response && (err.response.data || err.response.body)) || err);
                if(typeof payload === 'string') return payload;
                return JSON.stringify(payload, null, 2);
            } catch (e) {
                return String(err);
            }
        },
        escapeHtml(str) {
            if(typeof str !== 'string') return '';
            return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        },
        handleStripeOnboardingQuery() {
            try {
                const q = (this.$route && this.$route.query) ? this.$route.query : {};
                const state = (q.onboardingstate || '').toString();
                const msg = (q.onboardingmsg || '').toString();
                if(!state && !msg) return;
                const isSuccess = state.toLowerCase() === 'success';
                this.onboardingNotice = {
                    typeClass: isSuccess ? 'is-success' : 'is-danger',
                    iconClass: isSuccess ? 'fa-check-circle' : 'fa-times-circle',
                    message: msg || (isSuccess ? 'Stripe Onboarding erfolgreich.' : 'Stripe Onboarding fehlgeschlagen.'),
                };
                this.$nextTick(() => {
                    const newQuery = { ...q };
                    delete newQuery.onboardingstate;
                    delete newQuery.onboardingmsg;
                    this.$router.replace({ path: this.$route.path, query: newQuery });
                });
            } catch (e) {
                // no-op
            }
        },
    },
};
</script>



// WEBPACK FOOTER //
// src/views/components/campground-settings/tabs/PaymentTab.vue